<template>
  <SmTable
    :row-style="{ cursor: 'pointer' }"
    :style="{ height, overflow: 'auto' }"
    :data="filteredEmployees"
    :columns
    key-field="_id"
    :loading
    @row-click="handleRowClick">
    <template #status="{ row }">
      <el-tag
        v-if="row.end_date"
        type="danger"
        size="small"
        effect="plain"
        class="status-tag">
        {{ i18n.t('terminated') }}
      </el-tag>
      <el-tag
        v-else
        type="success"
        size="small"
        effect="plain"
        class="status-tag">
        {{ i18n.t('active') }}
      </el-tag>
    </template>
    <template #email="{ row }">
      <div class="email-cell">
        <Mail :size="14" class="email-icon" />
        <span class="email-text">{{ row.email }}</span>
      </div>
    </template>
    <template #start_date="{ row }">
      <div v-if="row.start_date" class="date-cell">
        <Calendar :size="14" class="date-icon" />
        <span class="date-text">
          {{ displayDate(new Date(row.start_date), { dateStyle: 'short' }) }}
        </span>
      </div>
    </template>
    <template #end_date="{ row }">
      <div v-if="row.end_date" class="date-cell">
        <CalendarX :size="14" class="date-icon terminated" />
        <span class="date-text terminated">
          {{ displayDate(new Date(row.end_date), { dateStyle: 'short' }) }}
        </span>
      </div>
    </template>
    <template #employee_number="{ row }">
      <p v-if="row.employee_number" class="employee-number">
        <Hash :size="14" class="icon" />
        {{ row.employee_number }}
      </p>
    </template>
    <template #country_code="{ row }">
      <p v-if="row.country_code" class="country-code">
        <Globe :size="14" class="icon" />
        {{ row.country_code }}
      </p>
    </template>
    <template #cost_center_allocations="{ row }">
      <p v-if="row.cost_center_allocations" class="cost-center">
        <PieChart :size="14" class="icon" />
        {{
          formatCostCenterAllocations(
            row.cost_center_allocations
          )
        }}
      </p>
    </template>
  </SmTable>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { Employee } from '@/client'
  import { useI18n } from 'vue-i18n'
  import { Column } from '../sm/SmTable.types'
  import { displayDate } from '@/utils/formatter/date'
  import { Mail, Calendar, CalendarX, Hash, Globe, PieChart } from 'lucide-vue-next'

  const i18n = useI18n()
  const props = defineProps<{
    employees: Employee[]
    loading: boolean
    searchquery: string
    height: string
  }>()

  const emit = defineEmits<{
    rowClick: [employee: Employee]
  }>()

  function handleRowClick(iRow: number, employee: Employee) {
    emit('rowClick', employee)
  }

  const filteredEmployees = computed(() => {
    if (props.searchquery == '') return props.employees
    return props.employees.filter((e) => {
      if (e.email.toLowerCase().includes(props.searchquery.toLowerCase()))
        return true
      if (e.name?.toLowerCase().includes(props.searchquery.toLowerCase()))
        return true
    })
  })

  // Helper function to get employee field from employee_external or application_specific
  const getEmployeeField = (employee: Employee, fieldName: string): any => {
    // Check in employee_external first
    if (employee.employee_external && employee.employee_external[fieldName]) {
      return employee.employee_external[fieldName]
    }
    // Check in application_specific
    if (
      employee.application_specific &&
      employee.application_specific[fieldName]
    ) {
      return employee.application_specific[fieldName]
    }
    return null
  }

  // Format cost center allocations dictionary
  const formatCostCenterAllocations = (
    allocations: Record<string, number> | null
  ): string => {
    if (!allocations) return ''
    return Object.entries(allocations)
      .map(([costCenterId, weight]) => `${costCenterId}: ${weight}`)
      .join(', ')
  }

  // Computed properties for checking if fields exist in any employee
  const hasPosition = computed(() =>
    props.employees.some(
      (employee) => employee.position && employee.position.trim() !== ''
    )
  )

  const hasStartDate = computed(() =>
    props.employees.some((employee) => employee.start_date)
  )

  const hasEndDate = computed(() =>
    props.employees.some((employee) => employee.end_date)
  )

  const hasEmployeeNumber = computed(() =>
    props.employees.some((employee) => employee.employee_number)
  )

  const hasCountryCode = computed(() =>
    props.employees.some((employee) => employee.country_code)
  )

  const hasCostCenterAllocations = computed(() =>
    props.employees.some((employee) => employee.cost_center_allocations)
  )

  const columns = computed<Column<Employee>[]>(() => {
    const baseColumns: Column<Employee>[] = [
      {
        key: 'email',
        label: i18n.t('email'),
        sortable: true,
        width: 1,
      },
      {
        key: 'name',
        label: i18n.t('name'),
        sortable: true,
        width: 1,
      },
      {
        key: 'supervisor_name',
        label: i18n.t('supervisor'),
        sortable: true,
        width: 1,
      },
    ]

    if (hasPosition.value) {
      baseColumns.push({
        key: 'position',
        label: i18n.t('position'),
        sortable: true,
        width: 1,
      })
    }

    if (hasStartDate.value) {
      baseColumns.push({
        key: 'start_date',
        label: i18n.t('hiredOn'),
        sortable: true,
        width: 1,
      })
    }

    if (hasEndDate.value) {
      baseColumns.push({
        key: 'end_date',
        label: i18n.t('terminatedOn'),
        sortable: true,
        width: 1,
      })
    }

    if (hasEmployeeNumber.value) {
      baseColumns.push({
        key: 'employee_number',
        label: i18n.t('employeeNumber'),
        sortable: true,
        width: 1,
      })
    }

    if (hasCountryCode.value) {
      baseColumns.push({
        key: 'country_code',
        label: i18n.t('countryCode'),
        sortable: true,
        width: 1,
      })
    }

    if (hasCostCenterAllocations.value) {
      baseColumns.push({
        key: 'cost_center_allocations',
        label: i18n.t('costCenterAllocations'),
        sortable: true,
        width: 1,
      })
    }

    return baseColumns
  })
</script>

<style scoped lang="scss">
  .status-tag {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.5px;
  }

  .email-cell,
  .date-cell {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .email-icon,
  .date-icon {
    color: var(--el-text-color-secondary);
    opacity: 0.7;
  }

  .email-text,
  .date-text {
    font-size: 0.875rem;
  }

  .terminated {
    color: var(--el-color-danger);
    opacity: 0.8;
  }

  .employee-number,
  .country-code,
  .cost-center {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    font-size: 0.875rem;
  }

  .icon {
    color: var(--el-text-color-secondary);
    opacity: 0.7;
  }

  /* Hover effects for table rows */
  :deep(.sm-table-row) {
    transition: background-color 0.2s ease;
  }

  :deep(.sm-table-row:hover) {
    background-color: var(--el-fill-color-light);
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .email-cell,
    .date-cell,
    .employee-number,
    .country-code,
    .cost-center {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.25rem;
    }

    .email-icon,
    .date-icon,
    .icon {
      display: none;
    }
  }
</style>
